import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import LoadingSpinner from './LoadingSpinner';
import Projects from './Projects';
import Videos from './Videos';
import About from './About';
import Services from './Services';
import TeamCountdown from './TeamCountdown';
import Contact from './Contact';
import Footer from './Footer';
import bannerVideo from './assets/Video.mp4';
import logoIcon from './assets/icon.png';
import banner2 from './assets/2.jpg';
import banner3 from './assets/3.jpg';

const Home = () => {
  const [scrollY, setScrollY] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [popupImages, setPopupImages] = useState([]);
  const [currentPopupImage, setCurrentPopupImage] = useState(0);
  const [bannerSlides, setBannerSlides] = useState([]);
  const [loadingBanners, setLoadingBanners] = useState(true);

  // Default banners if database is empty
  const defaultBanners = [
    {
      id: 'default-1',
      mediaType: 'video',
      mediaUrl: bannerVideo,
      tagline: 'Cinematic Excellence',
      subtitle: 'Creating Unforgettable Visual Stories',
      showButton: true,
      buttonText: 'Our Productions',
      buttonLink: '#projects',
      priority: 1,
      active: true
    }
  ];

  // Close mobile menu when clicking on a link
  const handleMenuClick = () => {
    setIsMenuOpen(false);
    setIsDropdownOpen(false);
    document.body.style.overflow = 'unset';
  };

  // Toggle dropdown on mobile
  const toggleDropdown = (e) => {
    e.preventDefault();
    setIsDropdownOpen(!isDropdownOpen);
  };

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu when clicking outside or when route changes
  useEffect(() => {
    if (isMenuOpen) {
      const handleClickOutside = (e) => {
        if (!e.target.closest('.nav-links') && !e.target.closest('.mobile-menu-btn')) {
          setIsMenuOpen(false);
          setIsDropdownOpen(false);
        }
      };

      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isMenuOpen]);

  // Fetch banner slides from Firebase
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const bannersSnapshot = await getDocs(collection(db, 'banners'));
        const banners = bannersSnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(banner => banner.active) // Only show active banners
          .sort((a, b) => a.priority - b.priority); // Sort by priority
        
        if (banners.length > 0) {
          setBannerSlides(banners);
        } else {
          // Use default banner if no banners in database
          setBannerSlides(defaultBanners);
        }
        setLoadingBanners(false);
      } catch (error) {
        console.error('Error fetching banners:', error);
        // Use default on error
        setBannerSlides(defaultBanners);
        setLoadingBanners(false);
      }
    };

    fetchBanners();
  }, []);

  // Fetch popup images from Firebase
  useEffect(() => {
    const fetchPopupImages = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'popupImages'));
        const images = querySnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(img => img.active); // Only show active images
        
        setPopupImages(images);
        
        // Show popup only if there are active images and user hasn't seen it
        const hasSeenPopup = sessionStorage.getItem('hasSeenPopup');
        if (images.length > 0 && !hasSeenPopup) {
          setShowWelcomePopup(true);
        }
      } catch (error) {
        console.error('Error fetching popup images:', error);
      }
    };

    fetchPopupImages();
  }, []);

  // Auto-advance popup images
  useEffect(() => {
    if (popupImages.length > 1 && showWelcomePopup) {
      const interval = setInterval(() => {
        setCurrentPopupImage((prev) => (prev + 1) % popupImages.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [popupImages.length, showWelcomePopup]);

  // Handle popup close
  const handleClosePopup = () => {
    setShowWelcomePopup(false);
    sessionStorage.setItem('hasSeenPopup', 'true');
  };

  // Auto-advance slides
  useEffect(() => {
    if (bannerSlides.length > 1) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % bannerSlides.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [bannerSlides.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // Services moved to separate component

  // Team members will be announced on February 14th, 2026
  // const team = [
  //   { name: 'Alex Chen', role: 'Creative Director', image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400' },
  //   { name: 'Maya Patel', role: 'AI Vision Lead', image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400' },
  //   { name: 'James Rodriguez', role: 'Head of Production', image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400' },
  //   { name: 'Sarah Kim', role: 'VFX Supervisor', image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400' },
  // ];

  return (
    <div className="app">
      {/* Welcome Popup */}
      {showWelcomePopup && popupImages.length > 0 && (
        <div className="welcome-popup-overlay" onClick={handleClosePopup}>
          <div className="welcome-popup-content" onClick={(e) => e.stopPropagation()}>
            <button className="welcome-popup-close" onClick={handleClosePopup}>
              ×
            </button>
            <div className="welcome-popup-image">
              <img 
                src={popupImages[currentPopupImage]?.url} 
                alt="Magic Moon Studios Announcement" 
              />
            </div>
            {popupImages.length > 1 && (
              <div className="popup-dots">
                {popupImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentPopupImage(index)}
                    className={`popup-dot ${index === currentPopupImage ? 'active' : ''}`}
                    aria-label={`Go to image ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className={`navbar ${scrollY > 50 ? 'scrolled' : ''}`}>
        <div className="container nav-content">
          <Link to="/" className="logo">
            <img src={logoIcon} alt="Magic Moon Studios" className="logo-icon-img" />
            <span className="logo-text">MAGIC MOON STUDIOS</span>
          </Link>
          
          <button 
            className="mobile-menu-btn" 
            onClick={(e) => {
              e.stopPropagation();
              setIsMenuOpen(!isMenuOpen);
              setIsDropdownOpen(false);
              document.body.style.overflow = isMenuOpen ? 'unset' : 'hidden';
            }}
          >
            {isMenuOpen ? '✕' : '☰'}
          </button>

          {/* Mobile menu backdrop */}
          {isMenuOpen && (
            <div 
              className="mobile-menu-backdrop" 
              onClick={(e) => {
                e.stopPropagation();
                setIsMenuOpen(false);
                setIsDropdownOpen(false);
                document.body.style.overflow = 'unset';
              }}
            />
          )}

          <ul className={`nav-links ${isMenuOpen ? 'active' : ''}`}>
            <li><a href="#home" onClick={handleMenuClick}>Home</a></li>
            <li><a href="#about" onClick={handleMenuClick}>About</a></li>
            <li><a href="#projects" onClick={handleMenuClick}>Projects</a></li>
            <li className={`dropdown ${isDropdownOpen ? 'active' : ''}`}>
              <a href="#services" onClick={toggleDropdown}>Services ▾</a>
              <ul className="dropdown-menu">
                <li><Link to="/film-production" onClick={handleMenuClick}>Film Production</Link></li>
                <li><Link to="/short-movies" onClick={handleMenuClick}>Short Movies</Link></li>
                <li><Link to="/academy" onClick={handleMenuClick}>Academy</Link></li>
                <li><Link to="/ads-making" onClick={handleMenuClick}>Ads Making</Link></li>
                <li><Link to="/digital-content" onClick={handleMenuClick}>Digital Content</Link></li>
                <li><Link to="/music-production" onClick={handleMenuClick}>Music Production</Link></li>
              </ul>
            </li>
            <li><a href="#team" onClick={handleMenuClick}>Team</a></li>
            <li><a href="#contact" className="contact-btn" onClick={handleMenuClick}>Contact</a></li>
          </ul>
        </div>
      </nav>

      {/* Full-Screen Banner Slider */}
      <section id="banner" className="relative w-full h-screen mobile-banner overflow-hidden">
        {loadingBanners ? (
          <div className="absolute inset-0 flex items-center justify-center bg-[#1a1a2e]">
            <LoadingSpinner size="large" message="Loading banner..." />
          </div>
        ) : (
          <>
            {/* Slides */}
            {bannerSlides.map((slide, index) => (
              <div
                key={slide.id}
                className={`absolute inset-0 transition-opacity duration-1000 ${
                  index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
                }`}
              >
                {/* Background Video or Image with Ken Burns Effect */}
                <div className="absolute inset-0 overflow-hidden">
                  {slide.mediaType === 'video' ? (
                    <video
                      src={slide.mediaUrl}
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <img
                      src={slide.mediaUrl}
                      alt={slide.tagline}
                      className="w-full h-full object-contain bg-black"
                      style={{ objectPosition: 'center' }}
                    />
                  )}
                </div>

                {/* Dark Overlay */}
                <div className="absolute inset-0 bg-black/50 z-10"></div>

                {/* Content */}
                <div className="absolute inset-0 z-20 flex items-end justify-start pb-16 pl-16">
                  <div className="text-left max-w-4xl">
                    <h1 className="banner-title text-5xl md:text-7xl font-bold text-white mb-6">
                      {slide.tagline}
                    </h1>
                    {slide.subtitle && (
                      <p className="banner-subtitle text-xl md:text-3xl text-white/90 mb-8">
                        {slide.subtitle}
                      </p>
                    )}
                    {slide.showButton && (
                      <a
                        href={slide.buttonLink}
                        className="banner-button inline-block px-8 py-4 bg-gradient-to-r from-[#B8860B] to-[#FFD700] text-[#1a1a2e] rounded-full font-semibold hover:scale-105 transition-transform duration-300"
                      >
                        {slide.buttonText}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* Navigation Dots */}
            {bannerSlides.length > 1 && (
              <div className="mobile-banner-dots absolute bottom-10 left-1/2 -translate-x-1/2 z-30 flex gap-3">
                {bannerSlides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`transition-all duration-300 rounded-full ${
                      index === currentSlide
                        ? 'bg-white w-12 h-3'
                        : 'bg-white/50 hover:bg-white/80 w-3 h-3'
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            )}

            {/* Scroll Down Indicator */}
            <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-30 animate-bounce hidden md:block">
              <div className="flex flex-col items-center gap-2 text-white/80">
                <span className="text-sm font-medium">Scroll Down</span>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </div>
            </div>
          </>
        )}
      </section>

      {/* Projects Section - Component */}
      <Projects searchQuery={searchQuery} setSearchQuery={setSearchQuery} />

      {/* Videos Section - Component */}
      <Videos />

      {/* About Section - Component */}
      <About />

      {/* Services Section - Component */}
      <Services />

      {/* Team Section - Countdown to Announcement */}
      <section id="team" className="team">
        <div className="container">
          <TeamCountdown />
        </div>
      </section>

      {/* Contact Section - Component */}
      <Contact />

      {/* Footer Component */}
      <Footer />
    </div>
  );
};

export default Home;
